import type { ReadonlyDeep } from 'type-fest';
import type { Tabs } from '@/stores/useTabsStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { isAfter } from 'date-fns';
import { toDate, toZonedTime } from 'date-fns-tz';
import _sortBy from 'lodash/sortBy';

import { getISODateString } from '@/utils/date';
import * as oneTrackingJobHelpers from '@/helpers/trackingJob/oneTrackingJob';
import {
  StatusesItemsValue,
  statusesItemsValues,
} from '@/constants/fieldItems';

export function filterJobsBySearch(
  filteredJobs: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi>,
  search: string,
): openapi.DashboardJobsTrackingContentOpenapi {
  search = search.toUpperCase();
  return filteredJobs.filter((job) => {
    return (
      job.rockhopper_id.toString().includes(search) ||
      job.customer.includes(search) ||
      job.ref1.includes(search) ||
      job.ref1_prompt.includes(search) ||
      job.stops[0].name.includes(search) ||
      job.stops[1].name.includes(search) ||
      (job.main_stop === 1 &&
        job.stops[0].suburb.toUpperCase().includes(search)) ||
      (job.main_stop === 1 &&
        job.stops[0].state.toUpperCase().includes(search)) ||
      (job.main_stop === 1 &&
        // TODO
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        (oneTrackingJobHelpers.getStopDriver(job, 1) ?? '')
          .toString()
          .includes(search)) ||
      (job.main_stop === 2 &&
        job.stops[1].suburb.toUpperCase().includes(search)) ||
      (job.main_stop === 2 &&
        job.stops[1].state.toUpperCase().includes(search)) ||
      (job.main_stop === 2 &&
        // TODO
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        (oneTrackingJobHelpers.getStopDriver(job, 2) ?? '')
          .toString()
          .includes(search))
    );
  }) as openapi.DashboardJobsTrackingContentOpenapi;
}

export function filterJobsByCategory(
  filteredJobs: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi>,
  category: Tabs['dashboard'],
  timeZone: string,
): openapi.DashboardJobsTrackingContentOpenapi {
  if (category === 'all') {
    return [...filteredJobs] as openapi.DashboardJobsTrackingContentOpenapi;
  }
  if (category === 'req4change') {
    return filteredJobs.filter(
      (job) => job.is_rfc,
    ) as openapi.DashboardJobsTrackingContentOpenapi;
  }
  if (category === 'cancelled') {
    return filteredJobs.filter(
      (job) => !!job.cancelled_at,
    ) as openapi.DashboardJobsTrackingContentOpenapi;
  }
  const userCurrentDate = toZonedTime(new Date(), timeZone);
  if (category === 'completed') {
    return filteredJobs.filter((job) => {
      if (job.cancelled_at) return false;
      if (job.completed_at) return true;
      if (job.stops[1].pod_time) return true;
      if (job.stops[1].arrived_at) return false;
      if (job.stops[0].departed_at) {
        // If service_area > 100 => this is split-to-base job
        if (job.service_area > 100) {
          const required2Date = toZonedTime(
            // TODO
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            toDate(job.stops[1].required_at ?? ''),
            timeZone,
          );
          // There is nothing to do with that job today => add to completed
          if (
            isAfter(
              getISODateString(required2Date),
              getISODateString(userCurrentDate),
            )
          ) {
            return true;
          }
          if (!job.allocated_at) return false;
        }
        if (job.delay_status === 2 && job.main_stop !== 1) return false;
        if (job.delay_status === 1 && job.main_stop !== 1) return false;
        if (job.is_priority && job.main_stop !== 1) return false;
        if (job.main_stop === 1) return true;
        return false;
      }
      return false;
    }) as openapi.DashboardJobsTrackingContentOpenapi;
  }
  if (category === 'inProgress') {
    return filteredJobs.filter((job) => {
      if (job.cancelled_at) return false;
      if (job.completed_at) return false;
      if (job.stops[1].pod_time) return false;
      if (job.stops[1].arrived_at) return true;
      if (job.stops[0].departed_at) {
        if (job.service_area > 100) {
          const required2Date = toZonedTime(
            // TODO
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            toDate(job.stops[1].required_at ?? ''),
            timeZone,
          );
          if (
            isAfter(
              getISODateString(required2Date),
              getISODateString(userCurrentDate),
            )
          ) {
            return false;
          }
          if (!job.allocated_at) return false;
        }
        if (job.delay_status === 2 && job.main_stop !== 1) return true;
        if (job.delay_status === 1 && job.main_stop !== 1) return true;
        if (job.is_priority && job.main_stop !== 1) return true;
        if (job.main_stop === 1) return false;
        return true;
      }
      if (job.stops[0].equipment_code ?? job.allocated_at) {
        if (job.delay_status === 2) return true;
        if (job.delay_status === 1) return true;
        if (job.is_priority) return true;
        return true;
      }
      if (!job.allocated_at) return false;
      return true;
    }) as openapi.DashboardJobsTrackingContentOpenapi;
  }
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (category === 'unallocated') {
    return filteredJobs.filter((job) => {
      if (job.cancelled_at) return false;
      if (job.completed_at) return false;
      if (job.stops[1].pod_time) return false;
      if (job.stops[1].arrived_at) return false;
      if (job.stops[0].departed_at) {
        if (job.service_area > 100) {
          const required2Date = toZonedTime(
            // TODO
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            toDate(job.stops[1].required_at ?? ''),
            timeZone,
          );
          if (
            isAfter(
              getISODateString(required2Date),
              getISODateString(userCurrentDate),
            )
          ) {
            return false;
          }
          // In case split-to-base job was not allocated yet
          if (!job.allocated_at) return true;
        }
        if (job.delay_status === 2 && job.main_stop !== 1) return false;
        if (job.delay_status === 1 && job.main_stop !== 1) return false;
        if (job.is_priority && job.main_stop !== 1) return false;
        if (job.main_stop === 1) return false;
        return false;
      }
      if (job.stops[0].equipment_code ?? job.allocated_at) return false;
      if (!job.allocated_at) return true;
      return false;
    }) as openapi.DashboardJobsTrackingContentOpenapi;
  }
  return [];
}

export interface CategoryJobs {
  'all': openapi.DashboardJobsTrackingContentOpenapi;
  'completed': openapi.DashboardJobsTrackingContentOpenapi;
  'inProgress': openapi.DashboardJobsTrackingContentOpenapi;
  'req4change': openapi.DashboardJobsTrackingContentOpenapi;
  'unallocated': openapi.DashboardJobsTrackingContentOpenapi;
  'cancelled': openapi.DashboardJobsTrackingContentOpenapi;
}

export function getCategoryJobs(
  filteredJobs: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi>,
  timeZone: string,
): CategoryJobs {
  return {
    all: filteredJobs as openapi.DashboardJobsTrackingContentOpenapi,
    completed: filterJobsByCategory(filteredJobs, 'completed', timeZone),
    inProgress: filterJobsByCategory(filteredJobs, 'inProgress', timeZone),
    req4change: filterJobsByCategory(filteredJobs, 'req4change', timeZone),
    unallocated: filterJobsByCategory(filteredJobs, 'unallocated', timeZone),
    cancelled: filterJobsByCategory(filteredJobs, 'cancelled', timeZone),
  };
}

export function filterJobsByStatuses(
  filteredJobs: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi>,
  statuses: readonly string[], // TODO readonly StatusesItemsValue[], // TODO only uniq items?
): openapi.DashboardJobsTrackingContentOpenapi {
  const statusesObj: Partial<Record<StatusesItemsValue, boolean>> = {};
  (statuses as unknown as typeof statusesItemsValues).forEach((status) => {
    statusesObj[status] = true;
  });
  let newFilteredJobs = [
    ...filteredJobs,
  ] as openapi.DashboardJobsTrackingContentOpenapi;

  /* eslint-disable dot-notation, @typescript-eslint/dot-notation */
  if (statusesObj['cant-schedule']) {
    newFilteredJobs = newFilteredJobs.filter((job) => !job.is_schedulable);
  }
  if (statusesObj['futile']) {
    newFilteredJobs = newFilteredJobs.filter((job) => job.is_futile);
  }
  if (statusesObj['delayed']) {
    newFilteredJobs = newFilteredJobs.filter((job) => job.delay_status >= 1);
  }
  if (statusesObj['split-to-base']) {
    newFilteredJobs = newFilteredJobs.filter((job) => {
      return oneTrackingJobHelpers.checkSplitToBaseWithoutSecondDriver(job);
    });
  }
  /* eslint-enable dot-notation, @typescript-eslint/dot-notation */

  return newFilteredJobs;
}

export function filterJobsByDrivers(
  filteredJobs: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi>,
  drivers: readonly number[],
): openapi.DashboardJobsTrackingContentOpenapi {
  return filteredJobs.filter((job) => {
    return (
      drivers.includes(oneTrackingJobHelpers.getStopDriver(job, 1)) ||
      drivers.includes(oneTrackingJobHelpers.getStopDriver(job, 2))
    );
  }) as openapi.DashboardJobsTrackingContentOpenapi;
}

export function filterJobsByAttributes(
  filteredJobs: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi>,
  attributes: readonly string[], // TODO attributes type
): openapi.DashboardJobsTrackingContentOpenapi {
  let newFilteredJobs = [
    ...filteredJobs,
  ] as openapi.DashboardJobsTrackingContentOpenapi;
  attributes.forEach((filter) => {
    // Filter Tech 1, 2 and 3 if ANY TECH clicked
    if (filter === 'TECH') {
      newFilteredJobs = newFilteredJobs.filter((job) => {
        const jobAttributes = oneTrackingJobHelpers.getJobAttributes(job);
        return (
          jobAttributes.includes('TECH1') ||
          jobAttributes.includes('TECH2') ||
          jobAttributes.includes('TECH3')
        );
      });
    } else {
      newFilteredJobs = newFilteredJobs.filter((job) => {
        const jobAttributes = oneTrackingJobHelpers.getJobAttributes(job);
        return jobAttributes.includes(filter);
      });
    }
  });
  return newFilteredJobs;
}

export function filterJobsByStates(
  filteredJobs: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi>,
  states: readonly string[], // TODO only uniq items?
): openapi.DashboardJobsTrackingContentOpenapi {
  return filteredJobs.filter((dashboardJob) => {
    return (
      (dashboardJob.main_stop === 1 &&
        states.includes(dashboardJob.stops[0].state)) ||
      (dashboardJob.main_stop === 2 &&
        states.includes(dashboardJob.stops[1].state))
    );
  }) as openapi.DashboardJobsTrackingContentOpenapi;
}

export function getDriversItems(
  filteredJobs: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi>,
): number[] {
  const driversItems: number[] = [];
  filteredJobs.forEach((dashboardJob) => {
    const driver1 = oneTrackingJobHelpers.getStopDriver(dashboardJob, 1);
    const driver2 = oneTrackingJobHelpers.getStopDriver(dashboardJob, 2);
    if (driver1 && !driversItems.includes(driver1)) {
      driversItems.push(driver1);
    }
    if (driver2 && !driversItems.includes(driver2)) {
      driversItems.push(driver2);
    }
  });
  driversItems.sort((a: number, b: number): number => {
    // TODO
    /* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */
    return +a - +b;
  });
  return driversItems;
}

export function getSortedJobs(
  filteredJobs: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi>,
): openapi.DashboardJobsTrackingContentOpenapi {
  let sortedFilteredJobs = _sortBy(filteredJobs, [
    (job) => !job.is_rfc,
  ]) as openapi.DashboardJobsTrackingContentOpenapi;
  sortedFilteredJobs = _sortBy(sortedFilteredJobs, [(job) => !job.is_priority]);
  return sortedFilteredJobs;
}
